import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useCompany } from '../../context/CompanyContext';
import ShareQR from '../common/ShareQR';

const {
  FiMenu,
  FiX,
  FiLogOut,
  FiUser,
  FiHome,
  FiFileText,
  FiImage,
  FiSettings,
  FiShare2,
  FiGlobe,
  FiUsers,
  FiSliders,
} = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const { isAuthenticated, user, isSuperuser, hasPermission, logout, openLoginModal } = useAuth();
  const { settings, globalSettings } = useSettings();
  const { company, subdomain } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const isWorkspace = !!company && !company.isBlocked;
  const isGlobalAdmin = isSuperuser && !isWorkspace;

  const globalNav = useMemo(() => {
    let parsed = [];
    try { parsed = JSON.parse(settings.navigation || '[]'); } catch { parsed = []; }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      parsed = [
        { id: 'home', label: 'Home', url: '/' },
        { id: 'links', label: 'Links', url: '/links' },
        { id: 'blog', label: 'Blog', url: '/blog' },
        { id: 'community', label: 'Community', url: '/community' },
      ];
    } else {
      parsed = parsed.map(item => {
        if (item.url === '/') return { ...item, label: 'Home' };
        if (item.url === '/forum') return { ...item, label: 'Community', url: '/community' };
        return item;
      });
      if (!parsed.some(i => i.url === '/')) {
        parsed.unshift({ id: 'home', label: 'Home', url: '/' });
      }
      if (!parsed.some(i => i.url === '/community')) {
        parsed.push({ id: 'community', label: 'Community', url: '/community' });
      }
    }
    return parsed;
  }, [settings.navigation]);

  const workspaceNav = useMemo(() => {
    if (!company || !subdomain) return [];
    const base = `/${subdomain}`;
    return [
      { id: 'home', label: 'Home', url: '/' },
      { id: 'links', label: 'Links', url: `${base}/links` },
      { id: 'blog', label: 'Blog', url: `${base}/blog` },
      { id: 'community', label: 'Community', url: `${base}/community` },
    ];
  }, [company, subdomain]);

  const nav = isWorkspace ? workspaceNav : globalNav;

  const logoUrl = globalSettings.app_logo_url || '/logo.png';
  const pageTitle = globalSettings.app_name || 'Vocaverse';

  const workspaceLogo = settings.app_logo_url || '/logo.png';
  const workspaceName = isWorkspace ? (settings.app_name || company.name) : null;

  useEffect(() => {
    if (!isMenuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setIsMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  const handleAuth = () => {
    if (isAuthenticated) logout();
    else openLoginModal();
    closeMenu();
  };

  const itemUrl = (item) => {
    if (item.type === 'page') return `/page/${item.slug}`;
    return item.url || '/';
  };

  const isExternal = (url) => url && (url.startsWith('http') || url.startsWith('//'));

  const isActive = (item) => {
    const url = itemUrl(item);
    if (url === '/') return location.pathname === url;
    return location.pathname === url || location.pathname.startsWith(`${url}/`);
  };

  const NavLink = ({ item, className, onClick, children }) => {
    if (item.type === 'link' && isExternal(item.url)) {
      return <a href={item.url} target="_blank" rel="noopener noreferrer" onClick={onClick} className={className}>{children || item.label}</a>;
    }
    return <Link to={itemUrl(item)} onClick={onClick} className={className}>{children || item.label}</Link>;
  };

  const DesktopNavItem = ({ item }) => {
    const active = isActive(item);
    const base = 'text-sm font-medium transition-colors';
    const className = `${base} ${active ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'}`;
    return <NavLink item={item} className={className} />;
  };

  const MenuButton = ({ onClick, icon, label, disabled }) => (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium transition-colors ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
    >
      <SafeIcon icon={icon} className="w-4 h-4" />
      {label}
    </button>
  );

  const menuItems = useMemo(() => {
    const items = [];
    const adminPrefix = isWorkspace && subdomain ? `/${subdomain}/admin` : '/admin';
    const homeLabel = 'Home';
    const homeUrl = '/';

    items.push({ id: 'home', label: homeLabel, url: homeUrl, icon: FiHome, show: true });

    if (isAuthenticated) {
      items.push({ id: 'dashboard', label: 'Dashboard', url: adminPrefix, icon: FiHome, disabled: isGlobalAdmin });
      if (hasPermission('articles')) {
        items.push({ id: 'articles', label: 'Blog Articles', url: `${adminPrefix}/articles`, icon: FiFileText, disabled: isGlobalAdmin });
      }
      if (hasPermission('media')) {
        items.push({ id: 'media', label: 'Media', url: `${adminPrefix}/media`, icon: FiImage, disabled: isGlobalAdmin });
      }
      if (hasPermission('menu')) {
        items.push({ id: 'menu', label: 'Menu', url: `${adminPrefix}/menu`, icon: FiSliders });
      }
      if (hasPermission('pages')) {
        items.push({ id: 'pages', label: 'Pages', url: `${adminPrefix}/pages`, icon: FiFileText });
      }
      if (hasPermission('siteSettings')) {
        items.push({ id: 'settings', label: 'Site settings', url: `${adminPrefix}/settings`, icon: FiSettings });
      }
      items.push({ id: 'account', label: 'Account settings', url: '/admin/account', icon: FiUser });
      if (isSuperuser) {
        items.push({ id: 'users', label: 'Manage users', url: '/admin/users', icon: FiUsers, disabled: isGlobalAdmin });
        items.push({ id: 'domains', label: 'Global settings', url: '/admin/domains', icon: FiGlobe });
      }
    }
    return items;
  }, [isWorkspace, company, subdomain, isAuthenticated, isSuperuser, hasPermission, isGlobalAdmin]);

  return (
    <header className="shadow-sm sticky top-0 z-50" style={{ backgroundColor: 'var(--brand-nav-bg, #ffffff)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center h-20">
          {/* Brand */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center gap-3">
              <img
                src={logoUrl}
                alt={`${pageTitle} Logo`}
                className="w-10 h-10 object-contain"
                onError={(e) => { e.currentTarget.src = '/logo.png'; }}
              />
              <span className="font-bold text-xl text-gray-900 hidden sm:block">{pageTitle}</span>
            </Link>
          </div>

          {/* Centered nav */}
          <div className="hidden md:flex items-center justify-center gap-6">
            {nav.map(item => (
              <DesktopNavItem key={item.id} item={item} />
            ))}
            {isWorkspace && (
              <Link to={`/${subdomain}`} className="flex items-center gap-2 pl-2 border-l border-gray-200">
                <img
                  src={workspaceLogo}
                  alt={workspaceName}
                  className="w-10 h-10 rounded-full object-cover border border-gray-200"
                  onError={(e) => { e.currentTarget.src = '/logo.png'; }}
                />
                <span className="text-sm font-medium text-gray-900">{workspaceName}</span>
              </Link>
            )}
          </div>

          {/* User area + hamburger */}
          <div className="flex items-center justify-end gap-2">
            {isAuthenticated && (
              <span className="hidden sm:block text-sm font-medium text-gray-700 pr-2">
                {user?.name || (user?.email ? user.email.slice(0, 5).toLowerCase() : 'Admin')}
              </span>
            )}

            <button
              onClick={handleAuth}
              className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors px-3 py-2 rounded-lg hover:bg-gray-50"
            >
              {isAuthenticated ? (
                <span className="flex items-center gap-1">
                  <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <SafeIcon icon={FiUser} className="w-4 h-4" />
                  <span className="hidden sm:inline">Login</span>
                </span>
              )}
            </button>

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(v => !v)}
                className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-5 h-5" />
              </button>

              {isMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50 py-1">
                  <MenuButton
                    onClick={() => { setQrOpen(true); closeMenu(); }}
                    icon={FiShare2}
                    label="QR code"
                  />

                  <div className="border-t border-gray-100 my-1" />

                  {menuItems.map(item => (
                    <MenuButton
                      key={item.id}
                      icon={item.icon}
                      label={item.label}
                      disabled={item.disabled}
                      onClick={() => { navigate(item.url); closeMenu(); }}
                    />
                  ))}

                  <div className="border-t border-gray-100 my-1" />

                  <MenuButton
                    onClick={handleAuth}
                    icon={isAuthenticated ? FiLogOut : FiUser}
                    label={isAuthenticated ? 'Logout' : 'Login'}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ShareQR url={typeof window !== 'undefined' ? window.location.origin : '/'} title={pageTitle} open={qrOpen} onOpenChange={setQrOpen} hideTrigger />
    </header>
  );
};

export default Header;
